import { useEffect, useState, useLayoutEffect, createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import user_api from 'utils/api/users';
import storage from 'utils/storage';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import AppBootLoader from './AppBootLoader'
import SlowConnectionTroubleshootHint from './SlowConnectionTroubleshootHint'
import SessionInactivityDialog from './dialogs/SessionInactivityDialog'
import useInactivityLogout from 'utils/hooks/useInactivityLogout'
import authDebugger from 'utils/authDebugger';
import { isPublicPath } from 'utils/routeAccess';
import Bugsnag from '@bugsnag/js';
import { NOT_AUTHORIZED_RESPONSE_EVENT, AUTH_LOGIN_SUCCESS_EVENT } from 'utils/authSessionEvents';
import {
  BOOT_APP_3G_SLOW_EVENT,
  getNavigatorConnection,
  isSlowBootConnectionHint,
} from 'utils/sparkleBootConnection';

// Create Authentication Context
const AuthContext = createContext();

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a SessionAuth provider');
  }
  return context;
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// Session cache to avoid redundant checks
let sessionCache = {
  token: null,
  userData: null,
  lastCheck: 0,
  isValid: false,
  checkInProgress: false
};

// Cache duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;
const TOKEN_REFRESH_THRESHOLD = 30 * 60 * 1000; // Refresh when 30 min left
const USER_FETCH_RETRY_DELAY_MS = 700;
/** Show slow-connection notice after this many consecutive transient failures (inclusive). */
const ROLE_VERIFY_SLOW_TOAST_AFTER_FAILURES = 3;
/** Also show the notice after this wall-clock wait (ms) since session check `try` started, or if main document / any recorded resource fetch took at least this long. */
const ROLE_VERIFY_SLOW_AFTER_ELAPSED_MS = 30_000;
/** Stacks above `#root-loader` (z-index 9999 in index.html). */
const SESSION_AUTH_SNACKBAR_Z = 10050;
const SLOW_ROLE_VERIFY_MESSAGE =
  'Slow internet connection detected. This may take awhile';

function buildMinimalUserFromToken(decodedToken, tokenUserId) {
  if (!tokenUserId) return null;
  return {
    _id: tokenUserId,
    displayName: decodedToken?.displayName || decodedToken?.name || 'User',
    role: typeof decodedToken?.role === 'number' ? decodedToken.role : null,
    company: decodedToken?.company,
    email: decodedToken?.email,
    sessionPartial: true,
  };
}

function parseStoredUser(rawUser) {
  if (!rawUser) return null;
  try {
    const parsed = typeof rawUser === 'string' ? JSON.parse(rawUser) : rawUser;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
}

/** Main HTML document network receive window (fetchStart → responseEnd), in ms — initial page resource transfer. */
function getMainDocumentResourceTransferMs() {
  try {
    const navList = performance.getEntriesByType?.('navigation');
    const nav = navList && navList.length ? navList[0] : null;
    if (nav && typeof nav.fetchStart === 'number' && typeof nav.responseEnd === 'number') {
      return Math.max(0, Math.round(nav.responseEnd - nav.fetchStart));
    }
    const t = performance.timing;
    if (t && t.fetchStart > 0 && t.responseEnd > 0) {
      return Math.max(0, t.responseEnd - t.fetchStart);
    }
  } catch (_e) {
    // ignore
  }
  return 0;
}

/** Largest recorded resource fetch duration on this page (scripts, styles, etc.), in ms. */
function getMaxResourceFetchDurationMs() {
  try {
    const entries = performance.getEntriesByType?.('resource') || [];
    let max = 0;
    for (const e of entries) {
      if (typeof e.duration === 'number' && e.duration > max) max = e.duration;
    }
    return Math.max(0, Math.round(max));
  } catch (_e) {
    return 0;
  }
}

export default function SessionAuth({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [roleVerified, setRoleVerified] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [roleVerifySlowConnectionOpen, setRoleVerifySlowConnectionOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const sessionCheckRef = useRef(null);
  const mountedRef = useRef(true);
  const initialCheckDoneRef = useRef(false);
  const notAuthorizedLogoutRef = useRef(false);
  const keepRootLoaderVisibleRef = useRef(false);
  const roleVerifySlowNoticeShownRef = useRef(false);

  // Snackbar handlers
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Clear all storage
      await storage.remove();
      
      // Reset session cache completely
      sessionCache = { 
        token: null, 
        userData: null, 
        lastCheck: 0, 
        isValid: false, 
        checkInProgress: false 
      };
      
      // Reset component state
      setIsAuthenticated(false);
      setUserData(null);
      setRoleVerified(false);
      roleVerifySlowNoticeShownRef.current = false;
      setRoleVerifySlowConnectionOpen(false);
      setChecked(true);
      setIsLoading(false);
      
      // Hide root loader
      if (window.hideRootLoader && typeof window.hideRootLoader === 'function') {
        try {
          window.hideRootLoader();
        } catch (error) {
          console.warn('Error hiding root loader:', error);
        }
      }
      
      // Clear centralized API caches
      if (user_api.clearUserCache) user_api.clearUserCache();
      if (user_api.clearProcessFlowCache) user_api.clearProcessFlowCache();
      if (user_api.clearGroupStoreCache) user_api.clearGroupStoreCache();
      if (window.userDataCache && typeof window.userDataCache.clear === 'function') {
        window.userDataCache.clear();
      }
      
      // Notify other tabs about logout
      try {
        localStorage.setItem('auth_logout', Date.now().toString());
        localStorage.removeItem('auth_logout');
      } catch (storageError) {
        console.warn('Could not notify other tabs about logout:', storageError);
      }
      
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigate to login even if there are errors
      navigate('/');
    }
  }, [navigate]);

  const inactivity = useInactivityLogout({ enabled: isAuthenticated, logout });

  useEffect(() => {
    const handleNotAuthorizedResponse = async () => {
      if (notAuthorizedLogoutRef.current) return;
      notAuthorizedLogoutRef.current = true;

      showSnackbar('Your session is no longer valid. Please log in again.', 'warning');

      try {
        await logout();
      } finally {
        notAuthorizedLogoutRef.current = false;
      }
    };

    window.addEventListener(NOT_AUTHORIZED_RESPONSE_EVENT, handleNotAuthorizedResponse);
    return () => window.removeEventListener(NOT_AUTHORIZED_RESPONSE_EVENT, handleNotAuthorizedResponse);
  }, [logout, showSnackbar]);

  // Enhanced token validation with refresh capability
  const validateAndRefreshToken = useCallback(async (token) => {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now();
      const tokenExpiry = decodedToken.exp * 1000;
      const timeUntilExpiry = tokenExpiry - currentTime;

      // Check if token is already expired
      if (timeUntilExpiry <= 0) {
        
        // Try to refresh the token by re-authenticating with stored credentials
        const rememberMe = storage.getRememberMe();
        if (rememberMe) {
          // Note: This would require a refresh endpoint on the backend
          // For now, we'll use the existing login flow
          return { isValid: false, shouldLogout: true };
        }
        
        return { isValid: false, shouldLogout: true };
      }

      // Check if token needs refresh soon (30 minutes before expiry)
      if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD) {
        // Could implement background refresh here
      }

      return { isValid: true, shouldLogout: false, token, decodedToken };
    } catch (error) {
      console.error('Token validation error:', error);
      return { isValid: false, shouldLogout: true };
    }
  }, []);

  // Optimized user data fetching with caching
  const fetchUserData = useCallback(async (userId, useCache = true, signal) => {
    const now = Date.now();
    
    if (!userId) {
      console.error('fetchUserData: userId is null or undefined');
      return { success: false, error: 'User ID is required' };
    }
    
    // Return cached data if valid and recent
    if (useCache && sessionCache.userData && sessionCache.lastCheck && 
        (now - sessionCache.lastCheck < CACHE_DURATION)) {
      
      // Use debugger to validate cached data
      const validation = authDebugger.validateUserData(sessionCache.userData, 'cachedData');
      
      if (validation.valid) {
        return { success: true, userData: sessionCache.userData };
      } else {
        // Clear invalid cached data
        sessionCache.userData = null;
        sessionCache.lastCheck = 0;
      }
    }

    try {
      const result = await Promise.race([
        user_api.get_user(userId, { signal, forceRefresh: !useCache }),
        new Promise((_, reject) =>
          setTimeout(() => reject(Object.assign(new Error('Request timeout'), { code: 'TIMEOUT' })), 8000)
        )
      ]);

      if (!result) {
        return { success: false, error: 'API returned null or undefined result', transient: true };
      }

      if (!result.ok) {
        const status = result.status;
        const problem = result.problem;
        let errorMessage = 'Failed to fetch user data.';
        try {
          const errorData = result.data || JSON.parse(result.text || '{}');
          errorMessage = errorData?.message || errorData?.error?.message || errorMessage;
        } catch (parseError) {
          // Continue with generic error message
        }
        const authFailure = status === 401;
        const transient =
          authFailure
            ? false
            : status === 0 ||
              status === 408 ||
              status === 429 ||
              (status >= 500 && status < 600) ||
              problem === 'NETWORK_ERROR' ||
              problem === 'TIMEOUT_ERROR' ||
              problem === 'CONNECTION_ERROR';
        if (!transient && !authFailure) {
          sessionCache.userData = null;
          sessionCache.lastCheck = 0;
        }
        return { success: false, error: errorMessage, transient, authFailure, httpStatus: status };
      }

      const userData = result.data;

      // Use debugger for comprehensive validation
      const validation = authDebugger.validateUserData(userData, 'freshAPIData');
      
      if (!validation.valid) {
        console.error('Invalid user data from API:', validation.issues.join(', '));
        sessionCache.userData = null;
        sessionCache.lastCheck = 0;
        return {
          success: false,
          error: `Invalid user data from API: ${validation.issues.join(', ')}`,
          authFailure: true,
        };
      }

      if (userData.isArchived) {
        return { success: false, isArchived: true };
      }

      // Update cache with validated data
      try {
        sessionCache.userData = { ...userData }; // Create a copy to avoid reference issues
        sessionCache.lastCheck = now;
      } catch (cacheError) {
        console.error('Error caching user data:', cacheError);
        // Continue without caching
      }

      return { success: true, userData };
    } catch (error) {
      if (error?.name === 'AbortError') {
        return { success: false, aborted: true };
      }
      const isTimeout = error?.message === 'Request timeout' || error?.code === 'TIMEOUT';
      if (isTimeout) {
        console.warn('User data fetch timed out; will retry or use cached session.');
        sessionCache.userData = null;
        sessionCache.lastCheck = 0;
      } else {
        console.error('User data fetch error:', error.message);
        sessionCache.userData = null;
        sessionCache.lastCheck = 0;
      }
      return {
        success: false,
        error: error.message,
        transient: isTimeout || error?.code === 'TIMEOUT',
      };
    }
  }, []);

  // Debounced session check to prevent rapid successive calls
  const performSessionCheck = useCallback(async (signal) => {
    // Prevent multiple simultaneous checks — don't reveal app until the running check completes.
    if (sessionCache.checkInProgress) {
      return;
    }

    // Skip re-entry after a completed check, except when the user just logged in without a full reload:
    // first run often finishes on /login with no token (userData never loaded). After login, token
    // exists but this ref still blocks — SPA navigation would never fetch userData until refresh.
    if (initialCheckDoneRef.current) {
      const token = await Promise.race([
        storage.getToken(),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000))
      ]);
      if (token) {
        initialCheckDoneRef.current = false;
      } else {
        return;
      }
    }

    sessionCache.checkInProgress = true;
    initialCheckDoneRef.current = true;
    keepRootLoaderVisibleRef.current = false;
    setIsLoading(true);
    
    // Show root loader during session validation
    // if (window.showRootLoader) {
    //   window.showRootLoader('Verifying your session...');
    // }

    try {
      const sessionSlowClockStartedAt = Date.now();
      const path = location.pathname;

      const isEmployeePublicPath = params.store &&
        (path === `/${params.store}/register` || path === `/${params.store}/onboard`);

      const isPublic = isPublicPath(path, params.store);

      // Get token with timeout protection
      const token = await Promise.race([
        storage.getToken(),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000))
      ]);
      console.log('token is',token)

      if (!token) {
        setIsAuthenticated(false);
        setRoleVerified(false);
        setRoleVerifySlowConnectionOpen(false);
        setChecked(true);
        setIsLoading(false);
        if (window.hideRootLoader) window.hideRootLoader();
        if (!isPublic) {
          navigate('/');
          await storage.remove();
        }
        return;
      }

      // Validate and potentially refresh token
      const tokenValidation = await validateAndRefreshToken(token);
      
      if (!tokenValidation.isValid) {
        setIsAuthenticated(false);
        setRoleVerified(false);
        setRoleVerifySlowConnectionOpen(false);
        setChecked(true);
        setIsLoading(false);
        if (window.hideRootLoader) window.hideRootLoader();
        if (!isPublic) {
          if (tokenValidation.shouldLogout) {
            showSnackbar('Your session has expired. Please log in again.', 'warning');
          }
          navigate('/');
          await storage.remove();
        } else {
          await storage.remove();
        }
        return;
      }

      const { decodedToken } = tokenValidation;
      setIsAuthenticated(true);
      setRoleVerified(false);
      roleVerifySlowNoticeShownRef.current = false;
      if (!(typeof window !== 'undefined' && window.__sparkleAppInitLoading)) {
        setRoleVerifySlowConnectionOpen(false);
      }

      // Normalize token user id across auth flows (some tokens use id, others _id/userId/sub)
      const tokenUserId = decodedToken?.id || decodedToken?._id || decodedToken?.userId || decodedToken?.sub
      const storedUserRaw = await storage.getUser()
      const storedUser = parseStoredUser(storedUserRaw)

      // Handle authenticated user navigation (redirect after user data is fetched)
      if (
        path === '/' ||
        path === '/login' ||
        path === '/store' ||
        path === '/store/create' ||
        path === '/store/onboard' ||
        path === '/signup'
      ) {
        if (!isEmployeePublicPath) {
          // Will redirect after user data is fetched
        }
      }

      // Fetch user data with caching; retry indefinitely on transient failures until success or hard failure
      const maybeShowSlowConnectionNotice = (failureCount) => {
        if (roleVerifySlowNoticeShownRef.current) return;
        const elapsedMs = Date.now() - sessionSlowClockStartedAt;
        const docTransferMs = getMainDocumentResourceTransferMs();
        const maxResourceMs = getMaxResourceFetchDurationMs();
        const resourceTransferMs = Math.max(docTransferMs, maxResourceMs);
        if (
          failureCount >= ROLE_VERIFY_SLOW_TOAST_AFTER_FAILURES ||
          elapsedMs >= ROLE_VERIFY_SLOW_AFTER_ELAPSED_MS ||
          resourceTransferMs >= ROLE_VERIFY_SLOW_AFTER_ELAPSED_MS
        ) {
          roleVerifySlowNoticeShownRef.current = true;
          setRoleVerifySlowConnectionOpen(true);
        }
      };
      let userDataResult = await fetchUserData(tokenUserId, true, signal);
      maybeShowSlowConnectionNotice(0);
      let consecutiveTransientFailures = 0;
      while (!signal.aborted) {
        if (!userDataResult || userDataResult.success || userDataResult.aborted) break;
        if (userDataResult.authFailure || userDataResult.isArchived) break;
        if (!userDataResult.transient) break;
        consecutiveTransientFailures += 1;
        maybeShowSlowConnectionNotice(consecutiveTransientFailures);
        await new Promise((r) =>
          setTimeout(r, USER_FETCH_RETRY_DELAY_MS * Math.min(consecutiveTransientFailures, 12))
        );
        maybeShowSlowConnectionNotice(consecutiveTransientFailures);
        userDataResult = await fetchUserData(tokenUserId, false, signal);
      }

      if (!userDataResult) {
        console.error('fetchUserData returned null or undefined');
        setIsAuthenticated(false);
        setChecked(true);
        setIsLoading(false);
        if (window.hideRootLoader) window.hideRootLoader();
        showSnackbar('Failed to fetch user data. Please log in again.', 'error');
        if (!isPublic) {
          navigate('/');
          await storage.remove();
        }
        return;
      }

      if (userDataResult.aborted) {
        roleVerifySlowNoticeShownRef.current = false;
        setRoleVerifySlowConnectionOpen(false);
        setIsAuthenticated(true);
        setUserData(buildMinimalUserFromToken(decodedToken, tokenUserId));
        setRoleVerified(false);
        keepRootLoaderVisibleRef.current = true;
        sessionCache.token = token;
        sessionCache.isValid = true;
        sessionCache.lastCheck = Date.now();
        return;
      }

      if (!userDataResult.success) {

        if (userDataResult.isArchived) {
          // Handle archived users - redirect to archived page and stay there until logout
          if (path !== '/archived') {
            setIsAuthenticated(false);

            // Redirect to archived page if not already there
            // Use tokenUserId as fallback if userData is not available
            const userId = userDataResult.userData?._id || tokenUserId;
            navigate('/archived', {
              state: {
                userId: userId,
                isArchived: true,
                role: userDataResult.userData?.role || 0,
                company: userDataResult.userData?.company || 'Unknown Company'
              }
            }, { replace: true })
            return;
          } else {
            // Already on archived page, allow access but provide user data for role checking
            setIsAuthenticated(false);
            // For archived users on archived page, we still need user data for role-based features
            if (userDataResult.userData) {
              setUserData({ ...userDataResult.userData, isArchived: true });
            } else {
              // Fallback: create minimal user data from decoded token
              setUserData({
                _id: tokenUserId,
                isArchived: true,
                role: decodedToken.role || 0,
                company: decodedToken.company || 'Unknown Company',
                displayName: decodedToken.displayName || 'Archived User'
              });
            }
            setChecked(true);
            setIsLoading(false);

            // Hide root loader
            if (window.hideRootLoader) {
              window.hideRootLoader();
            }
            return; // Allow access to archived page
          }
        }

        if (userDataResult.authFailure) {
          setIsAuthenticated(false);
          setRoleVerified(false);
          setChecked(true);
          setIsLoading(false);
          if (window.hideRootLoader) window.hideRootLoader();
          showSnackbar('Your session is no longer valid. Please log in again.', 'warning');
          if (!isPublic) {
            navigate('/');
            await storage.remove();
          }
          return;
        }

        if (userDataResult.transient) {
          sessionCache.token = token;
          sessionCache.isValid = true;
          sessionCache.lastCheck = Date.now();
        } else {
          showSnackbar(
            userDataResult.error || 'Profile could not be loaded. Some features may be limited.',
            'warning'
          );
          sessionCache.token = token;
          sessionCache.isValid = true;
          sessionCache.lastCheck = Date.now();
        }
      }

      const userData = userDataResult.success
        ? userDataResult.userData
        : userDataResult.transient &&
            sessionCache.userData &&
            authDebugger.validateUserData(sessionCache.userData, 'cachedData').valid
          ? sessionCache.userData
          : storedUser &&
              typeof storedUser === 'object' &&
              (storedUser._id === tokenUserId || String(storedUser._id) === String(tokenUserId))
            ? storedUser
          : buildMinimalUserFromToken(decodedToken, tokenUserId);

      if (!userData) {
        setIsAuthenticated(false);
        setChecked(true);
        setIsLoading(false);
        if (window.hideRootLoader) window.hideRootLoader();
        showSnackbar('Could not restore your session.', 'error');
        if (!isPublic) {
          navigate('/');
          await storage.remove();
        }
        return;
      }

      // Use debugger to validate user data (skip strict checks for token-only fallback)
      const validation = userData.sessionPartial
        ? { valid: true }
        : authDebugger.validateUserData(userData, 'sessionCheck-main');

      if (!validation.valid) {
        setIsAuthenticated(false);
        setChecked(true);
        setIsLoading(false);
        if (window.hideRootLoader) window.hideRootLoader();
        showSnackbar(`Invalid user data: ${validation.issues.join(', ')}. Please log in again.`, 'error');
        sessionCache.userData = null;
        sessionCache.isValid = false;
        if (!isPublic) {
          navigate('/');
          await storage.remove();
        }
        return;
      }

      // Safe user data setting with debugger protection
      try {
        setUserData(userData ? { ...userData } : null);
        const verifiedRole = Boolean(userDataResult.success);
        if (verifiedRole) {
          roleVerifySlowNoticeShownRef.current = false;
          setRoleVerifySlowConnectionOpen(false);
        }
        setRoleVerified(verifiedRole);
        if (!verifiedRole) {
          keepRootLoaderVisibleRef.current = true;
        }
      } catch (setUserDataError) {
        console.error('Error setting user data:', setUserDataError);
        setIsAuthenticated(false);
        setRoleVerified(false);
        setChecked(true);
        setIsLoading(false);
        if (window.hideRootLoader) window.hideRootLoader();
        showSnackbar('Failed to process user data. Please log in again.', 'error');
        if (!isPublic) {
          navigate('/');
          await storage.remove();
        }
        return;
      }

      // Handle dashboard redirection for authenticated users (with extensive null checks)
      if (
        path === '/' ||
        path === '/login' ||
        path === '/store' ||
        path === '/store/create' ||
        path === '/store/onboard' ||
        path === '/signup'
      ) {
        if (!isEmployeePublicPath) {
          try {
            // Use debugger's safe role access
            const userRole = authDebugger.safeGetUserRole(userData, 'dashboardRedirection', null);
            if (userRole === null) {
              return;
            }
            
            // Use same logic as routes.js getDashboardPath()
            const dashboardPath = userRole === 0 ? '/dashboard/app' : '/stores/app';

            navigate(dashboardPath);
            return;
          } catch (redirectError) {
            console.error('Error during dashboard redirection:', redirectError);
            // Continue execution instead of failing completely
          }
        }
      }

      // Check if user is on wrong dashboard and redirect to correct one
      try {
        if (userData) {
          const userRole = authDebugger.safeGetUserRole(userData, 'dashboardValidation', null);
          if (userRole === null) {
            return;
          }

          // Employee (role 0) should be on /dashboard/* paths
          // Admin/Store users (role >= 1) should be on /stores/* paths
          if (userRole === 0 && path.startsWith('/stores/')) {
            console.log('🔄 Employee (role 0) on admin dashboard, redirecting to employee dashboard');
            navigate('/dashboard/app');
            return;
          } else if (userRole >= 1 && path.startsWith('/dashboard/')) {
            console.log('🔄 Admin/Store user (role >= 1) on employee dashboard, redirecting to admin dashboard');
            navigate('/stores/app');
            return;
          }
        }
      } catch (roleCheckError) {
        console.error('Error during role-based dashboard validation:', roleCheckError);
        // Continue execution - don't break the app
      }

      // Handle archived user navigation
      if (userData && userData.isArchived) {
        // Archived users should only be on the archived page
        if (path !== '/archived') {
          console.log('🔄 Archived user on non-archived page, redirecting to archived page');
          navigate('/archived', {
            state: {
              userId: userData._id || tokenUserId,
              isArchived: true,
              role: userData.role || 0,
              company: userData.company || 'Unknown Company'
            }
          }, { replace: true });
          return;
        }
        // If already on archived page, allow access
      } else if (userData && !userData.isArchived) {
        // Non-archived users should not be on archived page
        if (path === '/archived') {
          console.log('🔄 Non-archived user on archived page, redirecting to appropriate dashboard');
          const userRole = authDebugger.safeGetUserRole(userData, 'archivedPageCheck', 0);
          const dashboardPath = userRole === 0 ? '/dashboard/app' : '/stores/app';
          navigate(dashboardPath, { replace: true });
          return;
        }
      } else if (!userData) {
        // If we don't have userData but we're not on a public path, redirect to login
        const isPublicFallback = isPublicPath(path, params.store);

        if (!isPublicFallback) {
          console.log('🔄 No user data available, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
      }

      // Update session cache
      sessionCache.token = token;
      sessionCache.isValid = true;
      sessionCache.lastCheck = Date.now();

    } catch (error) {
      console.error('Session check error occurred:', error);
      Bugsnag.notify(error);
      roleVerifySlowNoticeShownRef.current = false;
      setRoleVerifySlowConnectionOpen(false);
      setIsAuthenticated(false);
      setRoleVerified(false);
      setChecked(true);
      setIsLoading(false);
      if (window.hideRootLoader) {
        try { window.hideRootLoader(); } catch (e) { console.warn('Error hiding root loader:', e); }
      }
      if (!isPublicPath(location.pathname, params.store)) {
        navigate('/');
        await storage.remove();
      }
      showSnackbar('An authentication error occurred. Please try logging in again.', 'error');
    } finally {
      sessionCache.checkInProgress = false;
      if (mountedRef.current) {
        setChecked(true);
        setIsLoading(false);

        try {
          sessionStorage.removeItem('auth_logging_in');
        } catch (_error) {}

        // Hide root loader when session check is complete
        if (
          !keepRootLoaderVisibleRef.current &&
          window.hideRootLoader &&
          typeof window.hideRootLoader === 'function'
        ) {
          try {
            window.hideRootLoader();
          } catch (error) {
            console.warn('Error hiding root loader:', error);
          }
        }
      } else {
        // Component is unmounting, don't call window.hideRootLoader
        console.log('SessionAuth component unmounting, skipping root loader hide');
      }
    }
  }, [location.pathname, navigate, params.store, validateAndRefreshToken, fetchUserData, showSnackbar]);

  // Cross-tab session synchronization
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_logout') {
        // Another tab logged out, sync this tab
        setIsAuthenticated(false);
        setUserData(null);
        setRoleVerified(false);
        setRoleVerifySlowConnectionOpen(false);
        sessionCache = { token: null, userData: null, lastCheck: 0, isValid: false, checkInProgress: false };

        // Hide root loader
        if (window && window.hideRootLoader && typeof window.hideRootLoader === 'function') {
          try {
            window.hideRootLoader();
          } catch (error) {
            console.warn('Error hiding root loader:', error);
          }
        }

        console.log('redirect line 707')
        // Always redirect to login on logout, regardless of current path
        navigate('/', { replace: true });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // Initialize and clean up invalid cached data on mount
  useEffect(() => {
    // Clean up any invalid cached user data from previous sessions
    if (window.userDataCache && typeof window.userDataCache.validateAndCleanUserData === 'function') {
      window.userDataCache.validateAndCleanUserData();
    }
  }, []);

  // Main session check effect with debouncing
  useEffect(() => {
    const ac = new AbortController();
    // Clear any existing timeout
    if (sessionCheckRef.current) {
      clearTimeout(sessionCheckRef.current);
    }

    // Debounce session checks (300ms delay)
    sessionCheckRef.current = setTimeout(() => {
      performSessionCheck(ac.signal);
    }, 300);

    return () => {
      if (sessionCheckRef.current) {
        clearTimeout(sessionCheckRef.current);
      }
      ac.abort();
    };
  }, [performSessionCheck]);

  // Re-run session validation after SPA login (token stored without full page reload).
  useEffect(() => {
    const handleLoginSuccess = () => {
      initialCheckDoneRef.current = false;
      sessionCache.checkInProgress = false;
      if (sessionCheckRef.current) {
        clearTimeout(sessionCheckRef.current);
      }
      const ac = new AbortController();
      performSessionCheck(ac.signal);
    };

    window.addEventListener(AUTH_LOGIN_SUCCESS_EVENT, handleLoginSuccess);
    return () => window.removeEventListener(AUTH_LOGIN_SUCCESS_EVENT, handleLoginSuccess);
  }, [performSessionCheck]);

  // End the visible session when the signed token expires, even if the user
  // stays on one page and makes no further API requests.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const token = storage.getToken();
    if (!token) return undefined;
    let expiresAt;
    try {
      expiresAt = jwtDecode(token).exp * 1000;
    } catch (_error) {
      logout();
      return undefined;
    }
    const checkExpiry = () => {
      if (Date.now() >= expiresAt) logout();
    };
    const timeoutId = window.setTimeout(checkExpiry, Math.max(0, expiresAt - Date.now()));
    document.addEventListener('visibilitychange', checkExpiry);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', checkExpiry);
    };
  }, [isAuthenticated, logout]);

  // Track mount so we can set checked when session check completes; reset refs on unmount so remount runs full check
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      initialCheckDoneRef.current = false;
    };
  }, []);

  // App bootstrap (“Loading application…”) on slow network: same slow-connection toast + troubleshoot hint.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const applyBoot3gSlow = () => {
      setRoleVerifySlowConnectionOpen(true);
    };

    window.addEventListener(BOOT_APP_3G_SLOW_EVENT, applyBoot3gSlow);

    if (window.__sparkleAppInitLoading && isSlowBootConnectionHint(getNavigatorConnection())) {
      applyBoot3gSlow();
    }

    return () => {
      window.removeEventListener(BOOT_APP_3G_SLOW_EVENT, applyBoot3gSlow);
    };
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isAuthenticated,
    userData,
    roleVerified,
    isLoading,
    /** True while the persistent slow-connection warning is shown (session retries, slow resources, or 3g during app boot). */
    roleVerifySlowConnectionGuide: roleVerifySlowConnectionOpen,
    logout,
    showSnackbar,
    refreshUserData: async () => {
      // Force refresh user data. Do NOT depend on userData being present yet.
      // Derive the user id from the stored JWT (same source of truth used during session validation).
      try {
        const token = storage.getToken?.()
        if (!token) return { success: false, error: 'No token' }
        const decoded = jwtDecode(token)
        const id = decoded?.id || decoded?._id || decoded?.userId
        if (!id) return { success: false, error: 'No user id in token' }
        const result = await fetchUserData(id, false)
        if (result?.success && result.userData) {
          const refreshedUser = { ...result.userData }
          setUserData(refreshedUser)
          await storage.storeUser(refreshedUser)
        }
        return result
      } catch (e) {
        console.error('refreshUserData error:', e)
        return { success: false, error: e?.message || 'refreshUserData failed' }
      }
    }
  }), [isAuthenticated, userData, roleVerified, isLoading, roleVerifySlowConnectionOpen, logout, showSnackbar, fetchUserData]);

  return (
    <AuthContext.Provider value={contextValue}>
      <SessionInactivityDialog
        secondsRemaining={inactivity.secondsRemaining}
        onContinue={inactivity.continueSession}
        onLogout={logout}
      />
      {!checked ? (
        <AppBootLoader message="Checking Session..." ariaLabel="Checking session" />
      ) : (
        children
      )}
      {roleVerifySlowConnectionOpen ? <SlowConnectionTroubleshootHint /> : null}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: SESSION_AUTH_SNACKBAR_Z }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={roleVerifySlowConnectionOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={null}
        sx={{ zIndex: SESSION_AUTH_SNACKBAR_Z }}
        onClose={() => {}}
      >
        <Alert
          severity="warning"
          variant="filled"
          role="status"
          aria-live="polite"
          sx={{ alignItems: 'center' }}
        >
          {SLOW_ROLE_VERIFY_MESSAGE}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
} 
